import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Col, Row, Form, Spinner } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Autocomplete } from "@mui/material";
import { toast } from "react-toastify";
import BootstrapTextField from "Common/BootstrapTextField";
import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import {
  addhsnCode,
  fetchhsnCodeById,
  updatehsnCode,
} from "slices/Masters/hsn-code/thunk";
import { fetchGstByDropDown } from "slices/Masters/gst/thunk";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import { autocompleteDropdownStyles } from "Constants/Style";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

const HSNCodeForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const isEdit = Boolean(id);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [gstOptions, setGstOptions] = useState<[]>([]);
  const [initialData, setInitialData] = useState({
    GST: "",
    HSNCodeName: "",
    HSNDescripton: "",
  });

  // Fetch dropdown + data on edit
  useEffect(() => {
    dispatch(fetchGstByDropDown())
      .unwrap()
      .then((data: any) => {
        setGstOptions(data.data || []);
      })
      .catch(() => toast.error("Failed to fetch GST options"));

    if (isEdit && id) {
      setIsLoading(true);
      dispatch(fetchhsnCodeById(id))
        .unwrap()
        .then((data: any) => {
          data = data.data.data;

          setInitialData({
            GST: data.gst_id || "",
            HSNCodeName: data.hsn_code || "",
            HSNDescripton: data.hsn_code_description || "",
          });
        })
        .catch(() => toast.error("Failed to fetch HSN details"))
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      HSNCodeName: initialData.HSNCodeName || "",
      GST: initialData.GST || "",
      HSNDescripton: initialData.HSNDescripton || "",
    },
    validationSchema: Yup.object({
      HSNCodeName: Yup.string().trim().required("Please enter HSN Code"),
      GST: Yup.string().required("Please select GST"),
      // .matches(/^\d+$/, "HSN Code must contain numbers only"),

      // .matches(
      //   /^[A-Za-z ]+$/,
      //   "HSN Code  must contain only letters and spaces"
      // ),
      // HSNDescripton: Yup.string().required("Please enter HSN Description"),
    }),
    onSubmit: async (values) => {
      const payload = {
        hsn_code: values.HSNCodeName.trim(),
        gst_id: values.GST,
        hsn_code_description: values.HSNDescripton.trim(),
        // created_by: "Admin",
        // updated_by: "Admin",
      };

      setIsSubmitting(true);
      try {
        if (isEdit) {
          await dispatch(updatehsnCode({ id, ...payload })).unwrap();
          toast.success("HSN Code updated successfully");
        } else {
          await dispatch(addhsnCode(payload)).unwrap();
          clearTableState(TableKeys.MASTER.HSN_CODE);
          toast.success("HSN Code added successfully");
        }
        navigate("/master/hsn-codes");
      } catch (error: any) {
        toast.error(error.message || "Something went wrong");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(
    formik.errors,
    formik.submitCount,
    formik.isSubmitting,
  );

  return (
    <PageContainer>
      <PageBreadcrumb
        title={id ? "Edit HSN Code" : "Add HSN Code"}
        titleLink="/master/hsn-codes"
        breadcrumbs={[
          { label: "HSN Code", to: "/master/hsn-codes" },
          { label: id ? "Edit HSN Code" : "Add HSN Code", to: "" },
        ]}
        backNavigationPath={-1}
      />
      <hr />
      {isLoading && isEdit ? (
        <LoadingSpinner />
      ) : (
        <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row className="mb-3">
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">
                HSN Code <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="HSNCodeName"
                placeholder="Enter HSN Code"
                value={formik.values.HSNCodeName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!formik.errors.HSNCodeName && formik.touched.HSNCodeName
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.HSNCodeName}
              </Form.Control.Feedback>
            </Col>
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">
                GST% <span className="text-danger">*</span>
              </Form.Label>
              <Autocomplete
                options={gstOptions}
                getOptionLabel={(option: any) =>
                  typeof option === "string"
                    ? option
                    : `${option.gst_percentage}%`
                }
                value={
                  gstOptions.find(
                    (gst: any) => gst.gst_id === formik.values.GST,
                  ) || null
                }
                onChange={(event, selectedGST) => {
                  formik.setFieldValue("GST", selectedGST?.gst_id || "");
                }}
                onBlur={() => formik.setFieldTouched("GST", true)}
                ListboxProps={autocompleteDropdownStyles.ListboxProps}
                renderOption={(props, option) =>
                  autocompleteDropdownStyles.renderOption(
                    props,
                    option,
                    (opt) =>
                      typeof opt === "string"
                        ? opt
                        : String(opt.gst_percentage),
                  )
                }
                renderInput={(params) => (
                  <BootstrapTextField
                    {...params}
                    placeholder="Select GST"
                    name="GST"
                    error={formik.touched.GST && Boolean(formik.errors.GST)}
                    helperText={formik.touched.GST && formik.errors.GST}
                    sx={{
                      ...autocompleteDropdownStyles.inputSx,
                    }}
                    fullWidth
                    size="small"
                  />
                )}
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12}>
              <Form.Label className="fw-semibold">
                HSN Code Description
              </Form.Label>
              <Form.Control
                type="text"
                name="HSNDescripton"
                placeholder="Enter Description"
                value={formik.values.HSNDescripton}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!formik.errors.HSNDescripton && formik.touched.HSNDescripton
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.HSNDescripton}
              </Form.Control.Feedback>
            </Col>
          </Row>

          {/* Buttons */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelButton
              redirectTo="/master/hsn-codes"
              disabled={isSubmitting}
            />
            <PrimarySubmitButton
              label={id ? "Update" : "Submit"}
              isLoading={isSubmitting}
              loadingLabel={id ? "Updating..." : "Submitting..."}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default HSNCodeForm;
